import { isInFuturesApp } from '../../base/env/app/isInFuturesApp';
import { isInThsApp } from '../../base/env/app/isInThsApp';
import { isGPhone } from '../../base/env/platform/isGPhone';
import { isInIFindApp } from '../../base/env/app/isInIFindApp';
import { isInAInvestApp } from '../../base/env/app/isInAInvestApp';

type ShowCallback = () => void;

// 存储所有回调函数
const callbacks: ShowCallback[] = [];

// 统一的回调处理函数
const handleShow = () => {
  callbacks.forEach((callback) => {
    try {
      callback();
    } catch (error) {
      console.warn('执行onShow回调出错:', error);
    }
  });
};

// 是否已经注册过监听器
let isListenerRegistered = false;

// 存储事件监听器引用
let visibilityChangeHandler: (() => void) | null = null;

function isNativeApp(): boolean {
  return isInThsApp() || (isInFuturesApp() && isGPhone()) || isInIFindApp() || isInAInvestApp();
}

/**
 * 监听页面显示
 *
 * 该函数用于注册页面显示时的回调函数。在不同环境下有不同的实现：
 * - 在客户端环境（同花顺App或期货通Android端）下，使用 registerWebListener 注册回调
 * - 在其他环境下，使用 visibilitychange 事件监听页面显示
 *
 * 特点：
 * 1. 支持注册多个回调函数
 * 2. 避免重复注册相同的回调
 * 3. 只注册一次事件监听器
 * 4. 包含错误处理机制
 *
 * @param callback - 页面显示时的回调函数
 *
 * @example
 * ```ts
 * // 注册单个回调
 * onShow(() => {
 *   console.log('页面显示了');
 * });
 *
 * // 注册多个回调
 * onShow(() => {
 *   resumeVideo();
 * });
 * onShow(() => {
 *   refreshData();
 * });
 * ```
 */
export function onShow(callback: ShowCallback): void {
  // 防止重复添加相同的回调
  if (callbacks.includes(callback)) {
    return;
  }

  callbacks.push(callback);

  if (!isListenerRegistered) {
    if (isNativeApp()) {
      window.registerWebListener?.('onShow', handleShow);
    } else {
      // 其他环境
      visibilityChangeHandler = () => {
        if (!document.hidden) {
          handleShow();
        }
      };
      document.addEventListener('visibilitychange', visibilityChangeHandler);
    }
    isListenerRegistered = true;
  }
}